/**
 * Aska+ membership — the price, and whether a membership can actually be bought
 * right now. The twin of `seatPlan.ts`, and for the same reasons.
 *
 * SOURCE OF TRUTH IS THE SERVER (CLAUDE.md invariant 4): `membershipPlan.ts`
 * composes the plan from env, `/api/health` serves it as `askaPlusPlan`, and
 * this module is a LIVE BINDING seeded with the launch defaults and re-pointed
 * by `hydrateAskaPlusPlan()`, which the boot fetch in `askaServices.ts` calls.
 *
 * ── `live` IS THE ONLY THING A JOIN CTA MAY BRANCH ON ─────────────────────
 * `live: false` (the default, and what production serves until
 * ASKA_PLUS_BILLING_LIVE is armed) means: render the price + the DashPass saving
 * all you like — they are true and useful — but do NOT render a way to join,
 * because `POST /billing/aska-plus/checkout-session` will answer
 * `{configured:false}` and the person who clicked will conclude it's broken.
 *
 * ── AND ON THE NATIVE SHELL, NOT EVEN THAT ────────────────────────────────
 * Membership is WEB-ONLY for v1 (App Store IAP policy, same as seats). Use
 * `canJoinAskaPlus()`, which is `live && !isNativeShell()`, never `live` alone.
 */
import { isNativeShell } from '../mobile/lib/appMode'

export interface AskaPlusPlan {
  /** Monthly price in cents (1899 = $18.99). */
  priceCents: number
  currency: string
  interval: 'month'
  /** The discounted member fee percent (12). */
  memberFeePercent: number
  /** Member QC add-on discount in cents (0 until QC is priced). */
  qcDiscountCents: number
  /** Can a membership be bought right now? False while the rail is inert. */
  live: boolean
}

/** The launch defaults (docs/ASKA-PLUS-COST-MODEL.md §4). Display seed only —
 *  the server is authoritative and re-points this at boot. Nothing is ever
 *  charged from these numbers. */
export let ASKA_PLUS_PLAN: AskaPlusPlan = {
  priceCents: 1899,
  currency: 'usd',
  interval: 'month',
  memberFeePercent: 12,
  qcDiscountCents: 0,
  live: false,
}

/**
 * Re-point the plan from `/api/health`, then broadcast
 * `aska:plus-plan-changed`. VALIDATED, not trusted: a malformed payload leaves
 * the seed in place, and a missing `live` is FALSE — the safe direction is "no
 * join CTA", never "offer a join the server will refuse".
 */
export function hydrateAskaPlusPlan(raw: unknown): void {
  if (!raw || typeof raw !== 'object') return
  const p = raw as Partial<Record<keyof AskaPlusPlan, unknown>>
  const int = (v: unknown, fallback: number): number =>
    (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.trunc(v) : fallback)
  const next: AskaPlusPlan = {
    priceCents: int(p.priceCents, ASKA_PLUS_PLAN.priceCents),
    currency: typeof p.currency === 'string' && p.currency ? p.currency : ASKA_PLUS_PLAN.currency,
    interval: 'month',
    memberFeePercent: int(p.memberFeePercent, ASKA_PLUS_PLAN.memberFeePercent),
    qcDiscountCents: int(p.qcDiscountCents, ASKA_PLUS_PLAN.qcDiscountCents),
    live: p.live === true,
  }
  if (JSON.stringify(next) === JSON.stringify(ASKA_PLUS_PLAN)) return
  ASKA_PLUS_PLAN = next
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('aska:plus-plan-changed'))
  }
}

/**
 * THE gate for anything that lets a person JOIN Aska+ (spend money). Two
 * conditions, the second non-negotiable: the rail is armed server-side AND we
 * are not inside the native shell. Enforced against the source tree by
 * `askaPlus.guard.test.ts` exactly like `canPurchaseSeats()` — no web file may
 * branch on `ASKA_PLUS_PLAN.live`, and any file that touches
 * `/billing/aska-plus/checkout-session` or `/billing/aska-plus/portal` must
 * import this.
 */
export function canJoinAskaPlus(): boolean {
  return ASKA_PLUS_PLAN.live && !isNativeShell()
}

/** 1899 → "$18.99". Mirrors `formatMembershipMoney` on the server. */
export function formatAskaPlusPrice(cents: number = ASKA_PLUS_PLAN.priceCents, currency = ASKA_PLUS_PLAN.currency): string {
  const amount = (Number.isFinite(cents) ? cents : 0) / 100
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() })
      .format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`
  }
}

/** "$18.99/mo" — one string, one place, so the two shells describe the price the
 *  same way. */
export function askaPlusPriceLabel(): string {
  return `${formatAskaPlusPrice()}/mo`
}

/* ─────────────────────────── membership status ───────────────────────────── */

export type AskaPlusStatus = 'none' | 'active' | 'past_due' | 'canceled' | 'incomplete'

/** The membership block GET /me and GET /billing/aska-plus return. */
export interface AskaPlusMembership {
  status: AskaPlusStatus
  active: boolean
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  manageable: boolean
  lastInvoice: { status: string; amountCents: number | null; currency: string | null; hostedUrl: string | null; at: string | null } | null
}

/** A never-a-dead-end classification of the membership state, for the UI to
 *  branch on. Every state has a forward action; see the manage screen. */
export type MembershipUiState =
  | 'not_member'      // never joined / fully lapsed → offer join
  | 'active'          // paying → manage, don't re-offer
  | 'cancelling'      // active but cancel_at_period_end → offer reactivate
  | 'past_due'        // payment failing → update payment
  | 'incomplete'      // first payment never cleared → retry/join

export function membershipUiState(m: AskaPlusMembership | null | undefined): MembershipUiState {
  if (!m) return 'not_member'
  if (m.status === 'past_due') return 'past_due'
  if (m.status === 'incomplete') return 'incomplete'
  if (m.status === 'active' || m.active) return m.cancelAtPeriodEnd ? 'cancelling' : 'active'
  return 'not_member'
}
